import { Browser } from '@capacitor/browser';
import { AppLauncher } from '@capacitor/app-launcher';
import { isNativeNotWeb } from '@/utils/platform-check';

/**
 * Open an auth URL without ever rejecting.
 *
 * iOS rejects `Browser.open` with "Unable to display URL" whenever
 * SFSafariViewController cannot be presented — one is already up, the app is
 * backgrounded, or there is no presenting view controller. That rejection used
 * to escape the 401 interceptor and become the failure of whatever data request
 * happened to trigger the logout, so a homepage prefetch reported a URL-opening
 * error (12,892 occurrences in 60 days, hogc-one-mobile#1965).
 *
 * Order: the in-app browser, one retry once the current presentation has had a
 * chance to settle, then hand the URL to the OS, then the WebView itself.
 * AppLauncher routes through UIApplication.open, which needs no presenting view
 * controller — exactly the failure mode here — and the Auth0 callback is a
 * custom-scheme deep link, so control returns to the app either way.
 *
 * A logout passes `handOffToOs: false`: on native it stops after the in-app
 * browser, because handing a logout page to the OS sends the member out to
 * Safari for a session that is already cleared locally.
 *
 * @param {string} url
 * @param {{ source?: string, handOffToOs?: boolean }} [options] - `source` names
 *   the call site in logs
 * @returns {Promise<'browser'|'app-launcher'|'location'|null>} how it opened, or
 *   null if nothing worked. Never rejects.
 */

const RETRY_DELAY_MS = 400;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export async function openAuthUrl(url, { source = 'unknown', handOffToOs = true } = {}) {
  if (!url) return null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await Browser.open({ url, windowName: '_self' });
      return 'browser';
    } catch (error) {
      console.warn(
        `[SafeOpen] Browser.open failed (${source}, attempt ${attempt + 1}):`,
        error?.message,
      );
      if (attempt === 0) await sleep(RETRY_DELAY_MS);
    }
  }

  if (isNativeNotWeb()) {
    if (!handOffToOs) {
      console.warn(`[SafeOpen] In-app browser unavailable, not handing off to the OS (${source})`);
      return null;
    }
    try {
      // openUrl RESOLVES with { completed: false } when the OS has nothing to
      // handle the URL — it does not reject. Reporting success there would
      // leave loginWithRedirect waiting on a browser that never opened, so
      // only a completed hand-off counts; anything else falls through.
      const result = await AppLauncher.openUrl({ url });
      if (result?.completed !== false) return 'app-launcher';
      console.warn(`[SafeOpen] AppLauncher did not complete the hand-off (${source})`);
    } catch (error) {
      console.warn(`[SafeOpen] AppLauncher refused (${source}):`, error?.message);
    }
  }

  try {
    window.location.href = url;
    return 'location';
  } catch (error) {
    console.error(`[SafeOpen] No way to open URL (${source}):`, error?.message);
    return null;
  }
}
